/**
 * Two-pool account identity: personas carry global pool ids; tenant account
 * rows carry copy ids plus `global_pool_account_id`. Name matching is a
 * fallback only.
 */

function trimId(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  return s;
}

/** Pool id, copy id, then db id — pool first so persona.account_id can match. */
export function accountJoinIds(account) {
  const out = [];
  const seen = new Set();
  for (const value of [
    account?.global_pool_account_id,
    account?.account_id,
    account?.id,
    account?.accountId,
  ]) {
    const id = trimId(value);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function personaBelongsToAccount(persona, account) {
  if (!persona || !account) return false;
  const accountIds = new Set(accountJoinIds(account));
  for (const key of ['account_id', 'accountId', 'account_final_id', 'company_id']) {
    const id = trimId(persona[key]);
    if (id && accountIds.has(id)) return true;
  }
  const pName = String(persona.account_name || '').trim();
  const aName = String(account.account_name || account.name || '').trim();
  return Boolean(pName && aName && pName === aName);
}

export function indexAccountsForPersonaJoin(accounts) {
  const byPoolId = new Map();
  const byAccountId = new Map();
  const byDbId = new Map();
  for (const acc of accounts || []) {
    const pool = trimId(acc?.global_pool_account_id);
    if (pool) byPoolId.set(pool, acc);
    const copyId = trimId(acc?.account_id);
    if (copyId) byAccountId.set(copyId, acc);
    const dbId = trimId(acc?.id);
    if (dbId) byDbId.set(dbId, acc);
  }
  return { byPoolId, byAccountId, byDbId };
}

/**
 * Resolve the account row for a persona. Consults pool id, then copy id, then
 * db id. Caller supplies a name map if it wants the name fallback (aliases).
 */
export function resolveAccountForPersona(persona, indexes, nameMap, normalizeName) {
  const personAccId = trimId(persona?.account_id);
  if (personAccId) {
    const byPool = indexes?.byPoolId?.get(personAccId);
    if (byPool) return byPool;
    const byCopy = indexes?.byAccountId?.get(personAccId);
    if (byCopy) return byCopy;
    const byDb = indexes?.byDbId?.get(personAccId);
    if (byDb) return byDb;
  }
  if (nameMap && persona?.account_name) {
    const norm = typeof normalizeName === 'function'
      ? normalizeName(persona.account_name)
      : String(persona.account_name).trim().toLowerCase();
    if (norm && nameMap.has(norm)) return nameMap.get(norm);
  }
  return null;
}

/** Procurement-profile (or similar) row vs the account identifiers we have. */
export function recordMatchesAccount(record, { accountId, globalPoolAccountId, accountName } = {}) {
  if (!record) return false;
  const recordIds = new Set(
    [record.account_id, record.global_pool_account_id, record.id]
      .map(trimId)
      .filter(Boolean)
  );
  const pool = trimId(globalPoolAccountId);
  if (pool && recordIds.has(pool)) return true;
  const copyOrDb = trimId(accountId);
  if (copyOrDb && recordIds.has(copyOrDb)) return true;
  const name = String(accountName || '').trim();
  if (name && String(record.account_name || '').trim() === name) return true;
  if (name) {
    const recName = String(record.account_name || '').trim().toLowerCase();
    const needle = name.toLowerCase();
    if (recName && recName.includes(needle)) return true;
  }
  return false;
}
